import React from 'react';
import PropTypes from 'prop-types';
import { App } from '@meronex/app';
import { Icons } from '../../../common/Icons';

import IWarehouse from '@meronex/icons/mdc/MdcWarehouse';
import ICode from '@meronex/icons/fa/FaBarcode';
import ITXCount from '@meronex/icons/fa/FaExchangeAlt';
import IArchive from '@meronex/icons/en/EnArchive';
import IFactories from '@meronex/icons/mdc/MdcFactory';
import IiD from '@meronex/icons/mdc/MdcIdentifier';
import ITime from '@meronex/icons/bi/BiTimeFive';
import IValue from '@meronex/icons/fa/FaBalanceScaleLeft';
import ILocation from '@meronex/icons/en/EnLocation';

import { toast } from 'react-toastify';

import { resolveUnitRate, rateSourceLabel } from '../../api/pricing';

function StoreItemDetailsFields(props) {
  const {
    item,
    showStore,
    showTransaction,
    showDetails,
    permissions,
    showFactory,
    showUpdated,
    showId,
  } = props;

  const code = item.type.code;
  const storeName = item.store.name;

  const copyId = () => {
    try {
      navigator.clipboard.writeText(item._id);
      console.log(`${item._id} copied!`);
      toast.success('Item id copied successfully to clipboard!');
    } catch (e) {
      console.log(e);
      toast.error('Failed to copy item id to clipboard.');
    }
  };

  const renderItemPriceDetails = () => {
    if (permissions && permissions.viewPrices) {
      let unitRate, totalValue;

      // Rule lives in stores/api/pricing.js so the detail page and the Excel
      // export cannot drift apart (they previously did). `rateSource` drives
      // the label: editing the catalog price moves the number in a
      // non-dynamic store but not in a dynamic one, and without saying which
      // price this is there is nothing on screen to explain the difference.
      const resolved = resolveUnitRate(item);
      unitRate = resolved.rate;
      const rateSource = resolved.source;
      if (unitRate) {
        totalValue = item.quantity * unitRate;
      }

      if (!isNaN(unitRate) && !isNaN(totalValue) && showDetails) {
        return (
          <>
            <div>
              <span
                style={{
                  position: 'relative',
                  left: '-2px',
                }}>
                <Icons.Price size={16} />
              </span>
              <span
                style={{
                  position: 'relative',
                  top: '-4px',
                  left: '4px',
                }}>
                Rate:{' '}
                {(unitRate || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                AED/
                {item.type.unit}
                <span
                  title={
                    rateSource === 'weighted'
                      ? 'Weighted average cost from this store’s stock-in transactions. Change it with the Unit Rate action.'
                      : 'Catalog default price for this item type. This item has no stock-in cost yet.'
                  }
                  style={{
                    opacity: 0.95,
                    fontSize: '0.8em',
                    marginLeft: 6,
                    padding: '1px 6px',
                    borderRadius: 4,
                    border: '1px solid currentColor',
                    whiteSpace: 'nowrap',
                  }}>
                  ({rateSourceLabel(rateSource)})
                </span>
              </span>
            </div>
            <div>
              <span
                style={{
                  position: 'relative',
                  left: '-2px',
                }}>
                <IValue size={16} />
              </span>
              <span
                style={{
                  position: 'relative',
                  top: '-4px',
                  left: '4px',
                }}>
                Value:{' '}
                {(totalValue || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                AED
              </span>
            </div>
          </>
        );
      }
    }
    return <span />;
  };
  const fieldStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    whiteSpace: 'nowrap',
  };

  const separatorStyle = {
    margin: '0 5px',
    color: '#e2e8f0',
    fontSize: '10px',
  };

  const Separator = () => <span style={separatorStyle}>·</span>;

  // Collect inline fields
  const inlineFields = [];

  if (showStore) {
    inlineFields.push(
      <span key="store" style={fieldStyle}>
        <IWarehouse size={13} />
        <span>{App.utils.string.truncate(storeName, 20)}</span>
      </span>
    );
  }

  if (code) {
    inlineFields.push(
      <span key="code" style={fieldStyle}>
        <ICode size={13} />
        <span>{code}</span>
      </span>
    );
  }

  if (item.type.category) {
    inlineFields.push(
      <span key="category" style={fieldStyle}>
        <Icons.Category style={{ fontSize: '13px' }} />
        <span>{App.utils.string.truncate(item.type.category.name, 20)}</span>
      </span>
    );
  }

  if (item.location) {
    inlineFields.push(
      <span key="location" style={fieldStyle}>
        <ILocation size={13} />
        <span>{item.location.rack}.{item.location.shelf}.{item.location.level}</span>
      </span>
    );
  }

  if (showUpdated && item.updatedAt) {
    inlineFields.push(
      <span key="updated" style={fieldStyle}>
        <ITime size={13} />
        <span>{App.utils.time.timeAgo(item.updatedAt.date)}</span>
      </span>
    );
  }

  if (showTransaction) {
    inlineFields.push(
      <span key="tx" style={fieldStyle}>
        <ITXCount size={13} />
        <span><b>{item.transactionsCount || 0}</b> transactions</span>
      </span>
    );
  }

  if (showFactory && item.productionInfo) {
    inlineFields.push(
      <span key="factory" style={fieldStyle}>
        <IFactories size={13} />
        <span>{App.utils.string.truncate(item.productionInfo.factory.name, 20)}</span>
      </span>
    );
  }

  return (
    <div
      style={{
        marginTop: '0',
        fontSize: '12px',
        color: '#94a3b8',
      }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '1px',
        lineHeight: '1.6',
      }}>
        {inlineFields.map((field, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Separator />}
            {field}
          </React.Fragment>
        ))}
      </div>
      {renderItemPriceDetails()}
      {item.archived && (
        <div style={{ marginTop: '4px' }}>
          <IArchive size={13} />
          <span style={{ marginLeft: '6px' }}>
            {App.utils.time.fullFormat(item.archived.date)}
          </span>
        </div>
      )}
      {showId && (
        <div style={{ marginTop: '4px' }}>
          <IiD size={13} />
          <span style={{ marginLeft: '6px', cursor: 'pointer' }}>
            {item._id}{' '}
            <span className="link" onClick={copyId}>(copy)</span>
          </span>
        </div>
      )}
    </div>
  );
}
StoreItemDetailsFields.propTypes = {
  item: PropTypes.object,
  showStore: PropTypes.bool,
  showTransaction: PropTypes.bool,
  showDetails: PropTypes.bool,
  permissions: PropTypes.object,
  showFactory: PropTypes.bool,
  showUpdated: PropTypes.bool,
  showId: PropTypes.bool,
};

StoreItemDetailsFields.defaultProps = {
  showId: false,
};
export default StoreItemDetailsFields;
